import React from 'react'
import styled from 'styled-components/macro'
import { PageAnchor } from 'src/app/AppAuth/PageAnchor'
import palette from 'src/designSystem/variables/palette'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { CitationPill } from './CitationPill'

/** How many citation pills an entry shows before collapsing the rest into a badge. */
export const MAX_VISIBLE_CITATIONS = 20

export type CitationPillItem = { text: string; url: string }

const PillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 4px 0;
`

const MoreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #e2e6ea;
  color: #1f2532;
  font-size: 12px;
  font-weight: 600;
  cursor: default;
`

const OverflowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
  font-size: 12px;
`

const OverflowLink = styled(PageAnchor)`
  color: ${palette.blue[100]};
  text-decoration: none;
  word-break: break-word;
  &:hover,
  &:visited {
    color: ${palette.blue[100]};
  }
`

/**
 * Citation pills for one activity entry. A bulk change cites every object it touched, so
 * past MAX_VISIBLE_CITATIONS the rest collapse into a "+ N more" badge whose hover lists
 * them all.
 */
export const CitationPillList = ({
  citations,
  max = MAX_VISIBLE_CITATIONS,
}: {
  citations: CitationPillItem[]
  max?: number
}): JSX.Element | null => {
  if (citations.length === 0) return null
  const visible = citations.slice(0, max)
  const hidden = citations.slice(max)

  return (
    <PillList>
      {visible.map((c, idx) => (
        <CitationPill key={idx} text={c.text} url={c.url} />
      ))}
      {hidden.length > 0 && (
        <Tooltip
          id="activity-citation-overflow"
          overlayType={TooltipOverlayType.popover}
          placement={TooltipPositions.default}
          keepOpenOnHover
          tooltipBody={
            <OverflowList>
              {hidden.map((c, idx) => (
                <OverflowLink
                  key={idx}
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {c.text}
                </OverflowLink>
              ))}
            </OverflowList>
          }
        >
          <MoreBadge>+ {hidden.length} more</MoreBadge>
        </Tooltip>
      )}
    </PillList>
  )
}
